/**
 * UGC Testimonial Slider
 *
 * Vanilla JS, no dependencies. Each .ugc-slider instance is initialized
 * independently and scoped to its own root element — every DOM query below
 * runs against `this.root`, never `document` — so multiple copies of this
 * section can coexist on the same page without interfering with each other.
 *
 * Note: this is a plain .js asset (not .js.liquid), so it cannot read
 * {{ section.id }} directly. Instead, every section root carries a unique
 * id="ugc-{{ section.id }}" (set in the .liquid file) and a shared
 * "ugc-slider" class; this file discovers all instances via that class.
 */
(function () {
  'use strict';

  function UGCTestimonialSlider(root) {
    this.root = root;
    this.track = root.querySelector('.ugc-track');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-ugc-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-ugc-dot]'));
    this.prevBtn = root.querySelector('[data-ugc-prev]');
    this.nextBtn = root.querySelector('[data-ugc-next]');
    this.muteButtons = Array.prototype.slice.call(root.querySelectorAll('[data-ugc-mute]'));
    this.speed = parseInt(root.dataset.autoplaySpeed, 10) || 7000;
    this.currentIndex = 0;
    this.timer = null;
    this.isMuted = true; // sound is off by default

    if (this.slides.length === 0) return;

    this.bindEvents();
    this.goTo(0);

    if (this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  UGCTestimonialSlider.prototype.bindEvents = function () {
    var self = this;

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.stopAutoplay();
        self.goTo(parseInt(dot.dataset.index, 10));
        self.startAutoplay();
      });
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.stopAutoplay();
        self.goTo(self.currentIndex - 1);
        self.startAutoplay();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.stopAutoplay();
        self.goTo(self.currentIndex + 1);
        self.startAutoplay();
      });
    }

    this.muteButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        self.toggleMute();
      });
    });

    // Pause autoplay on hover (desktop) and on first touch (mobile).
    this.root.addEventListener('mouseenter', function () {
      self.stopAutoplay();
    });
    this.root.addEventListener('mouseleave', function () {
      self.startAutoplay();
    });
    this.root.addEventListener(
      'touchstart',
      function () {
        self.stopAutoplay();
      },
      { passive: true }
    );

    // Shopify theme editor integration: jump to the block being edited
    // and pause autoplay while it's selected.
    this.slides.forEach(function (slide, index) {
      slide.addEventListener('shopify:block:select', function () {
        self.stopAutoplay();
        self.goTo(index);
      });
      slide.addEventListener('shopify:block:deselect', function () {
        self.startAutoplay();
      });
    });
  };

  UGCTestimonialSlider.prototype.goTo = function (index) {
    var self = this;
    var total = this.slides.length;
    var normalized = ((index % total) + total) % total;
    this.currentIndex = normalized;

    this.slides.forEach(function (slide, i) {
      var isActive = i === normalized;
      slide.classList.toggle('is-active', isActive);

      if (isActive) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }

      var video = slide.querySelector('[data-ugc-video]');
      if (!video) return;

      if (isActive) {
        // Always start muted so the browser's autoplay policy never blocks
        // playback, then re-apply the user's sound preference immediately —
        // toggling .muted on a video that's already playing is always
        // allowed, even outside a user gesture.
        video.muted = true;
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            /* Autoplay blocked — poster image remains visible, no-op. */
          });
        }
        video.muted = self.isMuted;
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });

    this.dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === normalized);
    });

    this.updateMuteButtons();
  };

  UGCTestimonialSlider.prototype.toggleMute = function () {
    this.isMuted = !this.isMuted;

    var activeSlide = this.slides[this.currentIndex];
    var video = activeSlide && activeSlide.querySelector('[data-ugc-video]');
    if (video) {
      video.muted = this.isMuted;
    }

    this.updateMuteButtons();
  };

  UGCTestimonialSlider.prototype.updateMuteButtons = function () {
    var isMuted = this.isMuted;
    this.muteButtons.forEach(function (button) {
      button.classList.toggle('is-unmuted', !isMuted);
      button.setAttribute('aria-pressed', String(!isMuted));
      button.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
    });
  };

  UGCTestimonialSlider.prototype.startAutoplay = function () {
    if (this.slides.length <= 1 || this.timer) return;
    var self = this;
    this.timer = setInterval(function () {
      self.goTo(self.currentIndex + 1);
    }, this.speed);
  };

  UGCTestimonialSlider.prototype.stopAutoplay = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.ugc-slider').forEach(function (root) {
      new UGCTestimonialSlider(root);
    });
  });
})();
